/**
 * Library — sections/main-cad-library.liquid
 *
 * Progressive enhancement over a server-rendered, paginated grid:
 *   1. Loads the JSON index (templates/collection.cad-library-data.liquid) for the WHOLE source
 *      collection, 250 products per request, so search and filters never cover just page one.
 *   2. Product-name search + category (All / Lighting / Hardware), type and format filters, rendered
 *      client-side and sorted by SKU (natural order on the first variant SKU).
 *   3. One canonical analytics event per action, dispatched to whatever is present on the page
 *      (Shopify Customer Events, gtag, dataLayer). Never preventDefault: every link keeps working
 *      when analytics is missing, blocked by the bot guard, or declined via consent.
 *
 * Events (see docs/cad-3d-library.md):
 *   cad_library_resource_select  product_handle, product_title, product_type, format (revit | sketchup |
 *                                tearsheet | install_guide | viewer | video | ar | catalog), destination
 *                                (file_download | external_platform | product_page), placement, link_host,
 *                                platform?, entry_source, entry_medium, entry_campaign
 *   cad_library_product_click    product_handle, product_type, placement, entry_*
 *   cad_library_trade_interest   target (bundle | trade_program | help), placement, entry_*
 *   cad_library_search           query_length, results_count, group, types, formats, entry_*   (debounced)
 *   cad_library_index_error      status, entry_*                                           (index failed)
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('cad-library')) return;

  var UTM_KEY = 'db_cad_entry';
  var SEARCH_DEBOUNCE_MS = 250;
  var SEARCH_EVENT_DEBOUNCE_MS = 900;
  var CLIENT_PAGE_SIZE = 48;

  /* ------------------------------------------------------------------ utils */

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function normalize(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/["“”″]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function hostOf(url) {
    try { return new URL(url, window.location.href).host; } catch (e) { return ''; }
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // Natural compare on SKU chunks: "30110-10-1A" < "30110-12-1A", "10401" < "H10" < "H30".
  function skuChunks(sku) {
    return String(sku || '').toUpperCase().match(/\d+|[A-Z]+/g) || [];
  }
  function compareSku(a, b) {
    var ca = skuChunks(a.sku), cb = skuChunks(b.sku);
    if (!ca.length && !cb.length) return a.title.localeCompare(b.title);
    if (!ca.length) return 1;
    if (!cb.length) return -1;
    for (var i = 0; i < Math.max(ca.length, cb.length); i++) {
      var x = ca[i], y = cb[i];
      if (x === undefined) return -1;
      if (y === undefined) return 1;
      var xn = /^\d+$/.test(x), yn = /^\d+$/.test(y);
      if (xn && yn) { var d = parseInt(x, 10) - parseInt(y, 10); if (d) return d; }
      else if (xn !== yn) { return xn ? -1 : 1; }
      else if (x !== y) { return x < y ? -1 : 1; }
    }
    return a.title.localeCompare(b.title);
  }

  /* -------------------------------------------------------- entry attribution */

  function captureEntry() {
    var entry = null;
    try {
      var params = new URLSearchParams(window.location.search);
      var source = params.get('utm_source');
      if (source) {
        entry = {
          source: source.slice(0, 40),
          medium: (params.get('utm_medium') || '').slice(0, 40),
          campaign: (params.get('utm_campaign') || '').slice(0, 60),
          content: (params.get('utm_content') || '').slice(0, 60)
        };
        window.sessionStorage.setItem(UTM_KEY, JSON.stringify(entry));
      } else {
        var stored = window.sessionStorage.getItem(UTM_KEY);
        if (stored) entry = JSON.parse(stored);
      }
      if (!entry && document.referrer) {
        var refHost = hostOf(document.referrer);
        if (refHost && refHost !== window.location.host) {
          entry = { source: refHost.replace(/^www\./, ''), medium: 'referral', campaign: '', content: '' };
        }
      }
    } catch (e) { /* storage unavailable: attribution simply degrades */ }
    return entry || { source: 'direct', medium: 'none', campaign: '', content: '' };
  }

  /* ------------------------------------------------------------- analytics */

  function analyticsAllowed() {
    // Shopify's consent API is only present when a privacy banner / consent is configured.
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed() !== false;
      }
    } catch (e) { /* fall through */ }
    return true;
  }

  function track(eventName, params) {
    if (window.__dbBotDetected) return;
    var payload = Object.assign({}, params || {});
    try {
      // Shopify Customer Events (web pixels manage their own consent inside the sandbox)
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
      }
      if (!analyticsAllowed()) return;
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      }
      if (window.dataLayer && typeof window.dataLayer.push === 'function' && !window.dataLayer.__dbStubbed) {
        window.dataLayer.push(Object.assign({ event: eventName }, payload));
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  /* ---------------------------------------------------------- the element */

  var CadLibrary = function () { return Reflect.construct(HTMLElement, [], CadLibrary); };
  CadLibrary.prototype = Object.create(HTMLElement.prototype);
  CadLibrary.prototype.constructor = CadLibrary;

  CadLibrary.prototype.connectedCallback = function () {
    this.entry = captureEntry();
    this.index = null;          // full list of library products (loaded from the JSON view)
    this.indexPromise = null;
    this.indexError = null;
    this.state = { query: '', group: '', subs: [], formats: [], page: 1 };

    this.results = this.querySelector('[data-cad-results]');
    this.grid = this.querySelector('[data-cad-grid]');
    this.pagination = this.querySelector('[data-cad-pagination]');
    this.status = this.querySelector('[data-cad-status]');
    this.empty = this.querySelector('[data-cad-empty]');
    this.searchInput = this.querySelector('[data-cad-search]');
    this.searchClear = this.querySelector('[data-cad-search-clear]');
    this.serverGridHtml = this.grid ? this.grid.innerHTML : '';
    this.serverPages = this.grid ? parseInt(this.grid.getAttribute('data-cad-server-pages') || '1', 10) : 1;

    this.labels = {
      revit: this.getAttribute('data-revit-label') || '',
      sketchup: this.getAttribute('data-sketchup-label') || '',
      showSecondary: this.getAttribute('data-show-secondary') === 'true'
    };

    this.addEventListener('click', this.onClick.bind(this));

    if (this.getAttribute('data-index-url')) {
      this.bindControls();
      this.setAttribute('data-js-ready', '');
      this.readStateFromUrl();
      if (this.hasActiveState()) {
        this.applyState();
      } else {
        this.setStatus('Loading the full library…');
        var self = this;
        // Load the index right away: the default view is the whole library sorted by SKU.
        this.loadIndex().then(function () { if (!self.hasActiveState()) self.applyState(); }).catch(function () {
          self.setStatus('Search is unavailable right now, so the library is shown page by page in collection order.');
        });
      }
    }
  };

  /* --------------------------------------------------------------- controls */

  CadLibrary.prototype.bindControls = function () {
    var self = this;
    var form = this.querySelector('[data-cad-search-form]');
    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); self.state.query = self.searchInput.value; self.applyState(); });

    if (this.searchInput) {
      var onInput = debounce(function () { self.state.query = self.searchInput.value; self.applyState(); }, SEARCH_DEBOUNCE_MS);
      this.searchInput.addEventListener('input', function () {
        if (self.searchClear) self.searchClear.hidden = !self.searchInput.value;
        onInput();
      });
      this.searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.searchInput.value) { self.searchInput.value = ''; self.state.query = ''; self.applyState(); }
      });
    }
    if (this.searchClear) {
      this.searchClear.addEventListener('click', function () {
        self.searchInput.value = ''; self.searchClear.hidden = true; self.state.query = ''; self.applyState(); self.searchInput.focus();
      });
    }

    this.querySelectorAll('[data-cad-group]').forEach(function (chip) {
      chip.addEventListener('click', function () { self.setGroup(chip.getAttribute('data-cad-group') || ''); });
    });
    this.querySelectorAll('[data-cad-sub]').forEach(function (chip) {
      chip.addEventListener('click', function () { self.toggleChip('subs', chip.getAttribute('data-cad-sub'), chip); });
    });
    this.querySelectorAll('[data-cad-format-filter]').forEach(function (chip) {
      chip.addEventListener('click', function () { self.toggleChip('formats', chip.getAttribute('data-cad-format-filter'), chip); });
    });
    this.querySelectorAll('[data-cad-reset]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.reset(); });
    });
  };

  CadLibrary.prototype.setGroup = function (group) {
    this.state.group = group;
    this.state.subs = [];
    this.syncGroupUi();
    this.applyState();
  };

  // Press the active group chip, show only that group's type row, clear any type chips.
  CadLibrary.prototype.syncGroupUi = function () {
    var group = this.state.group, subs = this.state.subs;
    this.querySelectorAll('[data-cad-group]').forEach(function (chip) {
      chip.setAttribute('aria-pressed', (chip.getAttribute('data-cad-group') || '') === group ? 'true' : 'false');
    });
    this.querySelectorAll('[data-cad-sub-row]').forEach(function (row) {
      var mine = row.getAttribute('data-cad-sub-row') === group;
      row.hidden = !mine || row.getAttribute('data-cad-empty-row') === 'true';
      row.querySelectorAll('[data-cad-sub]').forEach(function (chip) {
        chip.setAttribute('aria-pressed', mine && subs.indexOf(chip.getAttribute('data-cad-sub')) !== -1 ? 'true' : 'false');
      });
    });
  };

  CadLibrary.prototype.toggleChip = function (key, value, chip) {
    var list = this.state[key];
    var idx = list.indexOf(value);
    if (idx === -1) list.push(value); else list.splice(idx, 1);
    chip.setAttribute('aria-pressed', idx === -1 ? 'true' : 'false');
    this.applyState();
  };

  CadLibrary.prototype.reset = function () {
    this.state = { query: '', group: '', subs: [], formats: [], page: 1 };
    if (this.searchInput) { this.searchInput.value = ''; }
    if (this.searchClear) this.searchClear.hidden = true;
    this.querySelectorAll('[data-cad-format-filter][aria-pressed="true"]').forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
    this.syncGroupUi();
    this.applyState();
    if (this.searchInput) this.searchInput.focus();
  };

  CadLibrary.prototype.readStateFromUrl = function () {
    try {
      var params = new URLSearchParams(window.location.search);
      var q = params.get('lib_q') || '';
      var group = params.get('lib_group') || '';
      if (group !== 'Lighting' && group !== 'Hardware') group = '';
      var subs = group ? (params.get('lib_type') || '').split(',').filter(Boolean) : [];
      var formats = (params.get('lib_format') || '').split(',').filter(Boolean);
      this.state.query = q; this.state.group = group; this.state.subs = subs; this.state.formats = formats;
      if (this.searchInput && q) { this.searchInput.value = q; if (this.searchClear) this.searchClear.hidden = false; }
      this.syncGroupUi();
      this.querySelectorAll('[data-cad-format-filter]').forEach(function (chip) {
        chip.setAttribute('aria-pressed', formats.indexOf(chip.getAttribute('data-cad-format-filter')) !== -1 ? 'true' : 'false');
      });
    } catch (e) { /* ignore */ }
  };

  CadLibrary.prototype.writeStateToUrl = function () {
    try {
      var url = new URL(window.location.href);
      ['lib_q', 'lib_group', 'lib_type', 'lib_format'].forEach(function (k) { url.searchParams.delete(k); });
      if (this.state.query) url.searchParams.set('lib_q', this.state.query);
      if (this.state.group) url.searchParams.set('lib_group', this.state.group);
      if (this.state.subs.length) url.searchParams.set('lib_type', this.state.subs.join(','));
      if (this.state.formats.length) url.searchParams.set('lib_format', this.state.formats.join(','));
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) { /* ignore */ }
  };

  /* ------------------------------------------------------------------ index */

  CadLibrary.prototype.loadIndex = function () {
    if (this.index) return Promise.resolve(this.index);
    if (this.indexPromise) return this.indexPromise;
    var self = this;
    var base = this.getAttribute('data-index-url');

    function fetchPage(page) {
      var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 'page=' + page;
      // No "Accept: application/json": Shopify then looks for a JSON-format template and answers 404.
      return fetch(url, { credentials: 'same-origin' }).then(function (res) {
        if (!res.ok) throw new Error('index_http_' + res.status);
        return res.json();
      });
    }

    this.indexPromise = fetchPage(1).then(function (first) {
      var pages = Math.max(1, parseInt(first.pages || 1, 10));
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(fetchPage(p));
      return Promise.all(rest).then(function (others) {
        var all = first.products.slice();
        others.forEach(function (d) { all = all.concat(d.products || []); });
        all.forEach(function (item) {
          item.sku = item.sku || '';
          item.group = item.group || (item.type === 'Hardware' ? 'Hardware' : 'Lighting');
          item.subtype = item.subtype || item.type || '';
          item._search = normalize(item.title + ' ' + item.handle.replace(/-/g, ' ') + ' ' + item.type + ' ' + item.subtype + ' ' + item.sku);
          item._formats = [];
          if (item.revit) item._formats.push('revit');
          if (item.sketchup) item._formats.push('sketchup');
          if (item.tearsheet) item._formats.push('tearsheet');
          if (item.install) item._formats.push('install');
          if (item.viewer) item._formats.push('viewer');
          if (item.video) item._formats.push('video');
        });
        // Default order: all lighting by SKU, then all hardware by SKU (filters and search keep it).
        all.sort(function (a, b) {
          var ga = a.group === 'Hardware' ? 1 : 0, gb = b.group === 'Hardware' ? 1 : 0;
          return ga - gb || compareSku(a, b);
        });
        self.index = all;
        self.indexError = null;
        self.updateChipCounts();
        return all;
      });
    }).catch(function (err) {
      self.indexPromise = null;
      self.indexError = err;
      track('cad_library_index_error', self.withEntry({ status: String(err && err.message || err) }));
      throw err;
    });
    return this.indexPromise;
  };

  CadLibrary.prototype.updateChipCounts = function () {
    if (!this.index) return;
    var self = this;
    var counts = { groups: { '': this.index.length }, subs: {}, formats: {} };
    this.index.forEach(function (item) {
      counts.groups[item.group] = (counts.groups[item.group] || 0) + 1;
      var key = item.group + '|' + item.subtype;
      counts.subs[key] = (counts.subs[key] || 0) + 1;
      item._formats.forEach(function (f) { counts.formats[f] = (counts.formats[f] || 0) + 1; });
    });

    this.querySelectorAll('[data-cad-group]').forEach(function (chip) {
      var g = chip.getAttribute('data-cad-group') || '';
      var n = counts.groups[g] || 0;
      chip.hidden = n === 0 && g !== '';
      var badge = chip.querySelector('[data-cad-count]');
      if (badge) { badge.textContent = '(' + n + ')'; badge.hidden = false; }
    });

    this.querySelectorAll('[data-cad-sub-row]').forEach(function (row) {
      var g = row.getAttribute('data-cad-sub-row');
      var known = [], visible = 0;
      row.querySelectorAll('[data-cad-sub]').forEach(function (chip) {
        var t = chip.getAttribute('data-cad-sub');
        known.push(t);
        var n = counts.subs[g + '|' + t] || 0;
        chip.hidden = n === 0;
        if (n) visible++;
        var badge = chip.querySelector('[data-cad-count]');
        if (badge) { badge.textContent = '(' + n + ')'; badge.hidden = false; }
      });
      // Sub-types present in the data but missing from the editor list still get a chip.
      Object.keys(counts.subs).sort().forEach(function (key) {
        var parts = key.split('|');
        if (parts[0] !== g || !parts[1] || known.indexOf(parts[1]) !== -1) return;
        var chip = document.createElement('button');
        chip.type = 'button'; chip.className = 'cad-chip'; chip.setAttribute('data-cad-sub', parts[1]);
        chip.setAttribute('aria-pressed', self.state.group === g && self.state.subs.indexOf(parts[1]) !== -1 ? 'true' : 'false');
        chip.innerHTML = escapeHtml(parts[1]) + ' <span class="cad-chip__count">(' + counts.subs[key] + ')</span>';
        chip.addEventListener('click', function () { self.toggleChip('subs', parts[1], chip); });
        row.appendChild(chip);
        visible++;
      });
      row.setAttribute('data-cad-empty-row', visible === 0 ? 'true' : 'false');
    });
    this.syncGroupUi();

    this.querySelectorAll('[data-cad-format-filter]').forEach(function (chip) {
      var f = chip.getAttribute('data-cad-format-filter');
      var n = counts.formats[f] || 0;
      chip.hidden = n === 0;
      var badge = chip.querySelector('[data-cad-count]');
      if (badge) { badge.textContent = '(' + n + ')'; badge.hidden = false; }
    });
  };

  /* ------------------------------------------------------------- filtering */

  CadLibrary.prototype.filter = function () {
    var q = normalize(this.state.query);
    var tokens = q ? q.split(' ') : [];
    var group = this.state.group, subs = this.state.subs, formats = this.state.formats;
    return this.index.filter(function (item) {
      if (group && item.group !== group) return false;
      if (subs.length && subs.indexOf(item.subtype) === -1) return false;
      for (var i = 0; i < formats.length; i++) if (item._formats.indexOf(formats[i]) === -1) return false;
      for (var t = 0; t < tokens.length; t++) if (item._search.indexOf(tokens[t]) === -1) return false;
      return true;
    });
  };

  CadLibrary.prototype.applyState = function () {
    var self = this;
    this.state.page = 1;
    this.writeStateToUrl();
    var active = this.hasActiveState();

    if (!active && !this.index) {
      this.restoreServerGrid();
      return;
    }

    if (!this.index) {
      this.setStatus(this.indexError ? '' : 'Loading the full library so search covers everything…');
      if (this.results) this.results.setAttribute('aria-busy', 'true');
      this.loadIndex().then(function () {
        if (self.results) self.results.removeAttribute('aria-busy');
        self.applyState();
      }).catch(function () {
        if (self.results) self.results.removeAttribute('aria-busy');
        self.restoreServerGrid();
        self.setStatus('Search is unavailable right now, so we are showing the library page by page instead. <a href="' + escapeHtml(self.getAttribute('data-source-url') || '#') + '">Reload</a> to try again.', true);
      });
      return;
    }

    var matches = this.filter();
    this.renderMatches(matches);
    if (active) this.trackSearch(matches.length);
  };

  CadLibrary.prototype.restoreServerGrid = function () {
    if (this.grid && this.grid.getAttribute('data-cad-client') === 'true') {
      this.grid.innerHTML = this.serverGridHtml;
      this.grid.removeAttribute('data-cad-client');
    }
    if (this.pagination) this.pagination.hidden = false;
    if (this.empty) this.empty.hidden = true;
    var more = this.querySelector('[data-cad-load-more]');
    if (more) more.remove();
    this.setStatus('Search covers the whole library. Type a product name or pick a category.');
  };

  CadLibrary.prototype.renderMatches = function (matches) {
    var self = this;
    if (!this.grid) return;
    if (this.pagination) this.pagination.hidden = true;
    var more = this.querySelector('[data-cad-load-more]');
    if (more) more.remove();

    if (!matches.length) {
      this.grid.innerHTML = '';
      this.grid.setAttribute('data-cad-client', 'true');
      if (this.empty) this.empty.hidden = false;
      this.setStatus('0 of ' + this.index.length + ' products match.');
      return;
    }
    if (this.empty) this.empty.hidden = true;

    var shown = Math.min(matches.length, CLIENT_PAGE_SIZE * this.state.page);
    var html = '';
    for (var i = 0; i < shown; i++) html += this.cardHtml(matches[i], i);
    this.grid.innerHTML = html;
    this.grid.setAttribute('data-cad-client', 'true');

    if (shown < matches.length) {
      var wrap = document.createElement('div');
      wrap.className = 'cad-library__load-more';
      wrap.setAttribute('data-cad-load-more', '');
      var btn = document.createElement('button');
      btn.type = 'button'; btn.className = 'button button--sm';
      btn.textContent = 'Show ' + Math.min(CLIENT_PAGE_SIZE, matches.length - shown) + ' more';
      btn.addEventListener('click', function () {
        self.state.page += 1;
        var firstNew = shown;
        self.renderMatches(matches);
        var cards = self.grid.querySelectorAll('[data-cad-card]');
        var target = cards[firstNew] && cards[firstNew].querySelector('a');
        if (target) target.focus();
      });
      wrap.appendChild(btn);
      this.results.appendChild(wrap);
    }
    if (this.hasActiveState()) {
      this.setStatus('Showing ' + shown + ' of ' + matches.length + ' matching products (library total ' + this.index.length + '), sorted by SKU. <a href="#" data-cad-reset-inline>Clear</a>', true);
    } else {
      this.setStatus('Showing ' + shown + ' of ' + this.index.length + ' products with 3D files, sorted by SKU.');
    }
    var inline = this.status && this.status.querySelector('[data-cad-reset-inline]');
    if (inline) inline.addEventListener('click', function (e) { e.preventDefault(); self.reset(); });
  };

  CadLibrary.prototype.hasActiveState = function () {
    return !!(this.state.query.trim() || this.state.group || this.state.subs.length || this.state.formats.length);
  };

  CadLibrary.prototype.cardHtml = function (item, i) {
    // Mirrors snippets/cad-library-card.liquid (L1): three rows of light-grey boxes, one item = full width.
    var revitLabel = this.labels.revit || item.revit_label || 'Revit (.rfa)';
    var sketchupLabel = this.labels.sketchup || item.sketchup_label || 'SketchUp';
    var img = item.image
      ? '<img class="cad-card__image" src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.image_alt || item.title) + '" loading="' + (i < 4 ? 'eager' : 'lazy') + '" width="600" height="600">'
      : '<span class="cad-card__image cad-card__image--empty" aria-hidden="true"></span>';
    function box(cls, href, attrs, icon, label, extra) {
      return '<a class="cad-card__file' + (cls ? ' ' + cls : '') + '" href="' + escapeHtml(href) + '" ' + attrs + '>' +
        '<span class="cad-card__file-icon" aria-hidden="true">' + icon + '</span><span class="cad-card__file-label">' + label + '</span>' + (extra || '') + '</a>';
    }
    // Packed: Revit, SketchUp, tear sheet, install guide, View in 3D, Watch video. Every box half width.
    var showDocs = this.labels.showSecondary;
    var boxes = [];
    if (item.revit) boxes.push(box('', item.revit, 'data-cad-action="resource" data-cad-format="revit" data-cad-destination="file_download" data-cad-placement="card"', ICONS.download, escapeHtml(revitLabel)));
    if (item.sketchup && item.sketchup_file) boxes.push(box('', item.sketchup, 'data-cad-action="resource" data-cad-format="sketchup" data-cad-destination="file_download" data-cad-placement="card"', ICONS.download, escapeHtml(sketchupLabel)));
    else if (item.sketchup) boxes.push(box('cad-card__file--external', item.sketchup, 'target="_blank" rel="noopener" data-cad-action="resource" data-cad-format="sketchup" data-cad-destination="external_platform" data-cad-placement="card"', ICONS.external, escapeHtml(sketchupLabel), '<span class="sr-only">(opens 3D Warehouse in a new tab)</span>'));
    if (showDocs && item.tearsheet) boxes.push(box('', item.tearsheet, 'target="_blank" rel="noopener" data-cad-action="resource" data-cad-format="tearsheet" data-cad-destination="file_download" data-cad-placement="card_secondary"', ICONS.doc, 'Tear sheet'));
    if (showDocs && item.install) boxes.push(box('', item.install, 'target="_blank" rel="noopener" data-cad-action="resource" data-cad-format="install_guide" data-cad-destination="file_download" data-cad-placement="card_secondary"', ICONS.doc, 'Install guide'));
    if (item.viewer) boxes.push(box('', item.url + '?3d=1', 'data-cad-action="resource" data-cad-format="viewer" data-cad-destination="product_page" data-cad-placement="card"', ICONS.cube, 'View in 3D'));
    if (item.video) boxes.push(box('', item.url + '?video=1', 'data-cad-action="resource" data-cad-format="video" data-cad-destination="product_page" data-cad-placement="card"', ICONS.play, 'Watch video'));

    var skuFamily = (item.sku || '').split('-')[0];
    var toolkit = '<div class="cad-card__toolkit">' + boxes.join('') + '</div>';

    var side = '';
    if (item.viewer && item.ar) {
      var arUrl = window.location.origin + item.url + '?ar=1';
      side = '<span class="cad-card__qrtile"><img src="https://api.qrserver.com/v1/create-qr-code/?size=256x256&amp;margin=1&amp;color=4A4A4A&amp;data=' + encodeURIComponent(arUrl) + '" alt="QR code: scan with your phone to see ' + escapeHtml(item.title) + ' in your space" width="56" height="56" loading="lazy"><span aria-hidden="true">Scan for AR</span></span>' +
        '<a class="cad-card__ar-btn" href="' + escapeHtml(item.url) + '?ar=1" data-cad-action="resource" data-cad-format="ar" data-cad-destination="product_page" data-cad-placement="card" aria-label="View ' + escapeHtml(item.title) + ' in your space">' + ICONS.ar + '</a>';
    }

    return '<article class="cad-card" data-cad-card data-handle="' + escapeHtml(item.handle) + '" data-type="' + escapeHtml(item.type) + '" data-sku="' + escapeHtml(item.sku || '') + '" data-sku-family="' + escapeHtml(skuFamily) + '" data-formats="' + escapeHtml(item._formats.join(' ')) + '">' +
      '<a class="cad-card__figure" href="' + escapeHtml(item.url) + '" data-cad-action="product_page" data-cad-placement="card_image" aria-label="' + escapeHtml(item.title) + '">' + img + '</a>' +
      '<div class="cad-card__info">' +
        '<div class="cad-card__head"><div class="cad-card__titles">' +
          '<h3 class="cad-card__title"><a href="' + escapeHtml(item.url) + '" data-cad-action="product_page" data-cad-placement="card_title">' + escapeHtml(item.title) + '</a></h3>' +
          (skuFamily ? '<p class="cad-card__sku text-xs text-subdued">SKU: <span>' + escapeHtml(skuFamily) + '</span></p>' : '') +
        '</div>' + side + '</div>' +
        toolkit +
      '</div></article>';
  };

  CadLibrary.prototype.setStatus = function (text, isHtml) {
    if (!this.status) return;
    if (isHtml) this.status.innerHTML = text; else this.status.textContent = text;
  };

  /* ------------------------------------------------------------- tracking */

  CadLibrary.prototype.withEntry = function (params) {
    return Object.assign({
      entry_source: this.entry.source,
      entry_medium: this.entry.medium,
      entry_campaign: this.entry.campaign,
      library_section: this.getAttribute('data-section-id') || ''
    }, params);
  };

  CadLibrary.prototype.trackSearch = debounce(function (resultsCount) {
    track('cad_library_search', this.withEntry({
      query_length: this.state.query.trim().length,
      results_count: resultsCount,
      group: this.state.group,
      types: this.state.subs.join('|'),
      formats: this.state.formats.join('|')
    }));
  }, SEARCH_EVENT_DEBOUNCE_MS);

  CadLibrary.prototype.onClick = function (event) {
    var link = event.target.closest('[data-cad-action]');
    if (!link || !this.contains(link)) return;

    var action = link.getAttribute('data-cad-action');
    var card = link.closest('[data-cad-card]');
    var product = card ? {
      product_handle: card.getAttribute('data-handle') || '',
      product_title: (card.querySelector('.cad-card__title') || {}).textContent || '',
      product_type: card.getAttribute('data-type') || ''
    } : { product_handle: '', product_title: '', product_type: '' };

    if (action === 'resource') {
      track('cad_library_resource_select', this.withEntry(Object.assign(product, {
        format: link.getAttribute('data-cad-format') || '',
        destination: link.getAttribute('data-cad-destination') || '',
        placement: link.getAttribute('data-cad-placement') || '',
        platform: link.getAttribute('data-cad-platform') || '',
        link_host: hostOf(link.href)
      })));
    } else if (action === 'product_page') {
      track('cad_library_product_click', this.withEntry({
        product_handle: product.product_handle,
        product_type: product.product_type,
        placement: link.getAttribute('data-cad-placement') || ''
      }));
    } else if (action === 'trade') {
      track('cad_library_trade_interest', this.withEntry({
        target: link.getAttribute('data-cad-target') || '',
        placement: link.getAttribute('data-cad-placement') || ''
      }));
    }
    // Never preventDefault: the browser follows the link whether or not any tracker exists.
  };

  // Bundle button is rendered by the theme's button snippet (no data attributes), so tag it here.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.cad-library__bundle-actions a.button');
    if (!btn) return;
    var lib = btn.closest('cad-library');
    if (lib && typeof lib.withEntry === 'function') {
      track('cad_library_trade_interest', lib.withEntry({ target: 'bundle', placement: 'intro' }));
    }
  });

  var ICONS = {
    download: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M8 2.5v8m0 0L4.75 7.25M8 10.5l3.25-3.25M2.5 13.5h11"/></svg>',
    external: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M6.5 3.5H3.5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V9.5M9.5 2.5h4v4M13.5 2.5 7.5 8.5"/></svg>',
    cube: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M8 1.75 14 5v6l-6 3.25L2 11V5l6-3.25Z"/><path d="M2 5l6 3.25L14 5M8 8.25v6"/></svg>',
    ar: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M2.5 5.5v-2a1 1 0 0 1 1-1h2M10.5 2.5h2a1 1 0 0 1 1 1v2M13.5 10.5v2a1 1 0 0 1-1 1h-2M5.5 13.5h-2a1 1 0 0 1-1-1v-2"/><path d="M8 5l2.6 1.5v3L8 11 5.4 9.5v-3L8 5Z"/></svg>',
    play: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><circle cx="8" cy="8" r="6.25"/><path d="M6.75 5.5v5L10.5 8 6.75 5.5Z"/></svg>',
    doc: '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M4 1.75h5.5L13 5.25v8.5a.75.75 0 0 1-.75.75H4a.75.75 0 0 1-.75-.75V2.5A.75.75 0 0 1 4 1.75Z"/><path d="M9.5 1.75V5.25H13M5.75 8.5h4.5M5.75 11h4.5"/></svg>'
  };

  if (window.customElements) window.customElements.define('cad-library', CadLibrary);
})();
